import React, { useState, useEffect } from "react";

const PaywallConfigModal = ({ protocol, handleProtocolChange, onClose }) => {
  // Use local state to manage potentially complex nested object updates smoothly
  const [paywallData, setPaywallData] = useState({
    plan: "",
    price: "",
    prompt: { not_paying: "", is_paying: "" },
  });

  // Sync local state when the protocol prop changes
  useEffect(() => {
    if (protocol) {
      setPaywallData({
        plan: protocol.plan || "",
        price: protocol.price || "",
        prompt: {
          not_paying: protocol.paywall_prompt?.not_paying || "",
          is_paying: protocol.paywall_prompt?.is_paying || "",
        },
      });
    }
  }, [protocol]);

  if (!protocol) return null;

  // Handler for nested prompt changes
  const handlePromptChange = (key, value) => {
    const updatedPrompt = { ...paywallData.prompt, [key]: value };
    setPaywallData((prev) => ({ ...prev, prompt: updatedPrompt }));
    // Pass the full updated paywall_prompt object to the main handler
    handleProtocolChange("paywall_prompt", updatedPrompt);
  };

  // Handler for top-level plan/price changes
  const handleFieldChange = (key, value) => {
    setPaywallData((prev) => ({ ...prev, [key]: value }));
    handleProtocolChange(key, value);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      {/* Increased size */}
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-semibold mb-6 flex-shrink-0">
          Configure Paywall
        </h2>

        {/* Main content area allowing vertical scroll if needed */}
        <div className="flex-grow overflow-y-auto pr-2 space-y-6">
          {/* Row 1: Plan & Price */}
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <label
                htmlFor="plan-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Plan (Stripe Price ID)
              </label>
              <input
                id="plan-input"
                type="text"
                value={paywallData.plan}
                onChange={(e) => handleFieldChange("plan", e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., price_1Lxxxxxxxxxxxx"
              />
              <p className="text-xs text-gray-500 mt-1">
                The Stripe Price ID for the subscription.
              </p>
            </div>
            <div className="flex-1">
              <label
                htmlFor="price-input"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Price Description
              </label>
              <input
                id="price-input"
                type="text"
                value={paywallData.price}
                onChange={(e) => handleFieldChange("price", e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., $30/month"
              />
              <p className="text-xs text-gray-500 mt-1">
                Human-readable price used in prompts like {"{price}"}.
              </p>
            </div>
          </div>

          {/* Row 2: Prompts */}
          <div className="flex flex-col md:flex-row gap-6">
            {/* Not Paying Prompt */}
            <div className="flex-1 flex flex-col">
              <label
                htmlFor="not-paying-prompt"
                className="block text-sm font-medium text-gray-700 mb-1 flex-shrink-0"
              >
                "Not Paying" Prompt
              </label>
              <textarea
                id="not-paying-prompt"
                value={paywallData.prompt.not_paying}
                onChange={(e) =>
                  handlePromptChange("not_paying", e.target.value)
                }
                className="w-full flex-grow p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm mb-1" // Reduced mb
                placeholder="Enter the prompt shown when the user is on the free plan..."
                rows={15} // Adjusted rows
              />
              <p className="text-xs text-gray-500 mt-1 flex-shrink-0">
                Guidance for the AI when interacting with a non-subscribed user.
              </p>
            </div>

            {/* Is Paying Prompt */}
            <div className="flex-1 flex flex-col">
              <label
                htmlFor="is-paying-prompt"
                className="block text-sm font-medium text-gray-700 mb-1 flex-shrink-0"
              >
                "Is Paying" Prompt
              </label>
              <textarea
                id="is-paying-prompt"
                value={paywallData.prompt.is_paying}
                onChange={(e) =>
                  handlePromptChange("is_paying", e.target.value)
                }
                className="w-full flex-grow p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm mb-1" // Reduced mb
                placeholder="Enter the prompt shown when the user is subscribed..."
                rows={15} // Adjusted rows
              />
              <p className="text-xs text-gray-500 mt-1 flex-shrink-0">
                Guidance for the AI when interacting with a subscribed user.
              </p>
            </div>
          </div>
        </div>

        {/* Footer / Close Button */}
        <div className="flex justify-end flex-shrink-0 border-t border-gray-200 pt-4 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaywallConfigModal;
